<template>
  <div data-view="threads">
    <router-view />

    <layout-logged-frame title="All deals" has-before>
      <template v-slot:header-actions>
        <Can action="store-campaign" subject="thread">
          <v-btn
            color="primary"
            variant="outlined"
            @click="$router.push({ name: 'threads-new-campaign' })"
          >
            <v-icon>add</v-icon>
            {{ $t('submit_campaign') }}
          </v-btn>
        </Can>

        <Can action="store-deal" subject="thread">
          <v-btn
            v-track:threads.click='{
              unitName: "submitDeal",
              unitType: "link",
              event: "form_tracking",
              isDisabled: isDisabledTracking
            }'
            color="primary"
            @click="$router.push({ name: 'threads-new-deal' })"
          >
            <v-icon>add</v-icon>
            {{ $t('submit_deal') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="[pagination.totalItems]"
        />
      </template>

      <template v-slot:content>
        <div v-if="isBanners">
          <Banner
            v-for="banner in banners"
            :key="banner.id"
            banner-type="success"
            icon="check_circle"
            :text="banner.summary"
            :title="banner.name"
            :button="{
              url: banner.action_button_url,
              label: banner.action_button_text,
            }"
            :platforms="banner?.countries"
            @redirect="onBannerRedirect(banner.id)"
            @dismiss="onBannerDismiss(banner.id)"
          />
        </div>

        <Filters
          :is-skeleton-loading="isSkeletonLoading"
          can-see-all
          class="mb-4"
          @filter="onFilter"
          @clearFilters="onClearFilters"
          ref="threadsFilters"
        />

        <Can action="view-widget" subject="limits">
          <limits-carousel />
        </Can>

        <table-threads
          v-if="canViewPSMThreads"
          :threads="threads"
          :pages="pages"
          :pagination="pagination"
          :is-loading="isLoadingThreads"
          :on-select-item="onSelectItem"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :on-change-item="onChangeItem"
          :on-toggle-comments="onToggleThreadComments"
          :on-update-thread="getThreads"
          :on-assign-thread="onAssignThread"
        />

        <table-threads-business-contact
          v-if="isBusinessContact"
          :threads="threads"
          :pages="pages"
          :pagination="pagination"
          :is-loading="isLoadingThreads"
          :on-select-item="onSelectItem"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :on-toggle-comments="onToggleThreadComments"
          :on-update-thread="getThreads"
        >
          <template v-slot:no-data-action>
            <Can action="store-deal" subject="thread">
              <v-btn
                v-track:threads.click='{
                  unitName: "submitDeal",
                  unitType: "link",
                  event: "form_tracking",
                  isDisabled: isDisabledTracking
                }'
                color="primary"
                @click="$router.push({ name: 'threads-new-deal' })"
              >
                <v-icon>add</v-icon>
                {{ $t('submit_deal') }}
              </v-btn>
            </Can>
          </template>
        </table-threads-business-contact>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import Filters from '../../components/forms/filters/Threads';
  import Banner from '../../components/Banner';
  import {isNum, isStr, isObj, isNonEmptyStr} from '@/utils';
  import navigationTabs from '../../config/threads/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'
  // TODO: Translate component§

  // TODO: Consider breaking down this view into smaller components
  export default {
    components: {
      Filters,
      Banner,
      NavigationTabs
    },
    data() {
      return {
        threads: [],
        businessContactThreads: [],
        pages: 0,
        isLoadingThreads: true,
        isSkeletonLoading: true,
        pagination: {
          order: 'desc', // there is condition in created()
          page: 1,
          rowsPerPage: 15,
          sortBy: 'start_date', // there is condition in created()
          totalItems: 0,
          search: '',
        },
        query: {},
        isUpdatingUser: false,
        isGettingPresentableBanners: false,
        navigationTabs
      };
    },
    computed: {
      canViewPSMThreads() {
        return (
          this.$ability.can('view-all-threads', 'thread') ||
          this.$ability.can('view-editors-threads', 'thread')
        );
      },
      isDisabledTracking() {
        return !this.$ability.can('track-actions', 'pixel');
      },
      countries() {
        return this.$store.getters['config/countries'];
      },
      loggedUser() {
        return this.$store.getters['auth/user'];
      },
      loggedUserRole() {
        return this.$store.getters['auth/role'];
      },
      isBusinessContact() {
        return (
          this.loggedUserRole.slug === 'marketing_agent' ||
          this.loggedUserRole.slug === 'business_contact' ||
          this.loggedUserRole.slug === 'business_admin'
        );
      },
      activeThread() {
        return this.$store.getters['threads/activeItem'];
      },
      commentPermissions() {
        return this.$store.getters['threads/commentPermissions'];
      },
      isBanners() {
        return this.canSeeBanners && this.banners.length !== 0;
      },
      banners() {
        return this.$store.getters['siteMessages/presentableItems'];
      },
      canSeeBanners() {
        return this.$ability.can('view-business-contact', 'banner');
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      countries() {
        this.getPresentableBanners();
      },
      currentRoute(to, from) {
        if (
          to.query?.status === 'success' &&
          (
            from.name === 'thread-edit' ||
            from.name === 'threads-new-deal' ||
            from.name === 'threads-new-campaign' ||
            from.name === 'threads-add-comment'
          ) &&
          to.name === 'threads'
        ) {
          this.getThreads();
        }
      },
    },
    mounted() {
      requestIdleCallback(() => {
        import('../threads/AddDeal.vue');
        import('../threads/AddCampaign.vue');
        import('../threads/EditThread.vue');
        import('../threads/AddComment.vue');
      });
    },
    created() {
      if (this.commentPermissions.length === 0) {
        this.getCommentPermissions();
      }
      this.getPresentableBanners();

      if (this.isBusinessContact) {
        this.pagination = {
          ...this.pagination,
          order: this.isBusinessContact ? 'desc' : 'asc',
          sortBy: this.isBusinessContact ? 'created_at' : 'start_date'
        }
      }
    },
    methods: {
      getThreads() {
        // eslint-disable-next-line prefer-const
        let { query } = this;

        const options = {
          ...query,
        };

        const { order } = options;

        this.isLoadingThreads = true;
        this.pagination.order = order;

        query = this.wrapQuery({
          ...options,
        });

        return this.$store
          .dispatch('threads/getItems', query)
          .then(({ data, meta }) => {
            this.pages = meta?.last_page || 0;
            this.threads = Array.isArray(data) ? [...data] : [];
            this.pagination.page = meta?.current_page || 0;
            this.pagination.totalItems = meta?.total || 0;
            this.pagination.lastPage = meta?.last_page || 0;
            this.totalPages = meta?.last_page || 0;

            return data;
          })
          .finally(() => {
            this.isSkeletonLoading = false;
            this.isLoadingThreads = false;
          });
      },
      // todo: isn't wrapQuery and getSearchQuery supposed to be the same thing?
      wrapQuery(options = {}) {
        const {
          countries,
          page = 1,
          order = 'desc',
          per_page = this.pagination.rowsPerPage,
          sort = this.pagination.sortBy,
          search = '',
          labels = [],
          business = -1,
          teamMembers = [],
          merchant_id = -1,
          category = '',
          date_filter = '',
          status = -1,
          accountTypes = [],
          priority = -1,
        } = options;

        return {
          countries,
          page,
          order,
          sort,
          search,
          labels,
          business,
          teamMembers,
          merchant_id,
          category,
          date_filter,
          per_page,
          status,
          accountTypes,
          priority,
        };
      },
      onAssignThread() {
        this.$refs.threadsFilters.getManagersManually();
      },
      getLimits() {
        if (this.$ability.can('view-widget', 'limits')) {
          return this.$store.dispatch('businessUsers/getLimits');
        }
        return [];
      },
      onSelectItem(thread) {
        this.$store.dispatch('threads/setActiveItem', { ...thread });
        this.$router.push({ name: 'thread-edit', params: { id: thread.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        this.pagination.sortBy = isNonEmptyStr(sortBy) ? sortBy : this.pagination.sortBy

        const { query } = this;
        this.query = {
          ...query,
          page,
          order,
          sort: this.pagination.sortBy,
        };
        this.getThreads();
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order } = pagination;
        this.query = {
          ...query,
          page,
          order,
          sort: pagination.sortBy,
        };

        this.getThreads();
      },
      onFilter(filters) {
        const searchQuery = this.getSearchQuery(filters);

        this.query.page = 1;

        this.query = {
          ...this.query,
          ...searchQuery,
        };

        this.getThreads();
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = {sort: sortBy};

        this.getThreads();
      },
      onChangeItem({ action, id }, thread) {
        const query = {
          id: thread.id,
        };

        if (action === 'user') {
          query.user = isNum(id) ? id : this.loggedUser.id;
        } else if (action === 'partnership_manager' || action === 'editor') {
          query.queue = action;
        } else if (action === 'comment') {
          this.$store.dispatch('threads/setActiveItem', { ...thread });
          return this.$router.push({ name: 'threads-add-comment', params: { id: thread.id } });
        }

        return this.$store.dispatch('threads/assignThread', query).then(() => {
          this.onAssignThread();
          this.getThreads();
        }).catch(err => err);
      },
      getSearchQuery({
        countries,
        search,
        labels,
        teamMembers,
        business,
        shop,
        category,
        dates,
        status,
        accountTypes,
        priority
      }) {
        const query = {};

        query.countries = countries;
        query.search = search;
        query.labels = labels?.map((l) => l.id) ?? [];
        query.business = isObj(business) ? business.id : -1;
        query.teamMembers = teamMembers;
        query.merchant_id = isObj(shop) ? shop.id : -1;
        query.category = isObj(category) ? category.code : '';
        query.date_filter = isStr(dates) ? dates : '';
        query.status = isNum(status) ? status : -1;
        query.priority = isObj(priority) ? priority.id : -1;
        query.accountTypes = accountTypes?.map(({ code }) => code) ?? [];

        return query;
      },
      onToggleThreadComments(thread, onComplete = () => {}) {
        if (thread.commentsRetrieved) {
          return onComplete();
        }

        const query = {
          id: thread.id,
          filter: 'all',
        };

        return this.$store
          .dispatch('threads/getComments', query)
          .then((comments) => {
            this.setThreadComments(thread, comments);
            return onComplete();
          });
      },
      setThreadComments({ id }, comments) {
        const { threads } = this;

        this.threads = threads.map((thread) => ({
          ...thread,
          comments: thread.id === id ? comments : thread.comments,
          commentsRetrieved: thread.id === id || !!thread.commentsRetrieved,
        }));
      },
      getCommentPermissions() {
        return this.$store.dispatch('threads/getCommentPermissions');
      },
      getPresentableBanners() {
        if (this.isGettingPresentableBanners || !this.canSeeBanners) {
          return;
        }
        this.isGettingPresentableBanners = true

        return this.$store
          .dispatch('siteMessages/getPresentableItems')
          .catch((err) => err)
          .finally(() => this.isGettingPresentableBanners = false);
      },
      onBannerRedirect(id) {
        return this.$store.dispatch('siteMessages/trackRedirect', { id });
      },
      onBannerDismiss(id) {
        return this.$store.dispatch('siteMessages/setDismissed', { id });
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

[data-view='threads'] {
  aside {
    [data-component-group='threads-dropdown'] {
      .trigger {
        .v-icon {
          margin: 0 0 0 10px;
        }
      }
    }
  }

  .v-datatable {
    min-width: 1200px;
  }

  .select-actions {
    display: inline-block;

    .heading-separator {
      margin: 0 10px 0 6px !important;
    }

    .title-box {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin: 0 0 0 6px;
    }
  }

  .selected-count {
    font-size: $text-size-xl + 0.125rem;
    color: map.get($colors, primary);
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 0.4px;
    font-feature-settings: 'pnum' on, 'lnum' on;
  }

  .trigger.assign-user {
    font-size: $text-size-sm;
    text-transform: none;
    margin: 0 0 0 -10px;

    &:before {
      background: transparent !important;
    }

    .v-icon {
      margin: 0 4px 0 0;
    }
  }

  .trigger.refresh-table {
    width: auto;
    min-width: 20px;
  }

  [data-component-group='thread-panels'][data-component='status'] {
    .trigger.view-url {
      margin-left: -16px !important;
    }

    .trigger.assign-user {
      margin-left: -12px;
    }
  }
}
</style>
